import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ModalProps } from '../../types';
import Button from './Button';

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-5xl',
    // Phone: full screen. md+: nearly the whole window; capped height so compose can pin the footer (R-01).
    full: 'max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:max-w-none max-md:rounded-none md:max-w-[min(98vw,96rem)] md:h-[min(97vh,calc(100dvh-1rem))] md:max-h-[min(97vh,calc(100dvh-1rem))] flex flex-col',
  };
  const isFull = size === 'full';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        {/* Background overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-500/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Full size: no outer page-scroll — inner compose owns scroll so Send stays reachable. */}
        <div className={`fixed inset-0 ${isFull ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div
            className={`flex min-h-full items-center justify-center text-center ${
              isFull ? 'p-0 md:p-2' : 'p-4'
            }`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-modal duration-[320ms]"
              enterFrom="opacity-0 translate-y-4 scale-[0.97]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-180"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <Dialog.Panel
                className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-2xl text-left align-middle bg-white shadow-modal ${
                  isFull ? 'p-0' : 'p-6'
                }`}
              >
                {/* Header */}
                <div
                  className={`flex shrink-0 items-center justify-between ${
                    isFull
                      ? 'border-b border-primary-500/10 px-4 py-2 sm:px-6'
                      : 'mb-4'
                  }`}
                >
                  {title && (
                    <Dialog.Title
                      as="h3"
                      className={`font-semibold leading-6 text-primary-500 ${
                        isFull ? 'text-base' : 'text-lg'
                      }`}
                    >
                      {title}
                    </Dialog.Title>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="!p-2 ml-auto"
                  >
                    <XMarkIcon className="w-5 h-5 text-primary-500" />
                  </Button>
                </div>

                {/* Content */}
                <div
                  className={
                    isFull
                      ? 'flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-3 pt-2 sm:px-6'
                      : undefined
                  }
                >
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Confirmation Modal Component
export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  loading = false
}) => {
  const iconColors = {
    danger: 'text-error-600 bg-error-500/10',
    warning: 'text-warning-600 bg-warning-500/10',
    info: 'text-primary-600 bg-primary-500/10'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconColors[variant]}`}>
            {variant === 'danger' && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {variant === 'warning' && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {variant === 'info' && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm text-neutral-800">{message}</p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button 
            variant={variant === 'danger' ? 'danger' : 'primary'} 
            onClick={onConfirm}
            loading={loading}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default Modal; 